import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowDown, ArrowUp, RotateCw, Search, SlidersHorizontal, TriangleAlert, X,
} from 'lucide-react';
import { getStudents, STUDENT_SEARCH_MAX_LENGTH } from '../../api/students360';
import Dropdown from '../../components/Dropdown/Dropdown';
import Modal from '../../components/Modal/Modal';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import Pagination from '../../components/Pagination/Pagination';
import SearchableCourseSelect from '../../components/SearchableCourseSelect/SearchableCourseSelect';
import { STUDENT_TIME_ZONE, nowFrom } from '../../components/Student360/time';
import { usePermissions } from '../../hooks/usePermissions';
import { useI18n } from '../../i18n/I18nContext';
import { formatTime } from '../../utils/datetime';
import TableScroll from '../Students/TableScroll';
import useIsPhone from '../Students/useIsPhone';
import DirectorySkeleton from './DirectorySkeleton';
import DirectoryTable, { columnsFor } from './DirectoryTable';
import {
  activeFilterCount,
  defaultOrderFor,
  parseDirectoryQuery,
  patchDirectoryQuery,
  toSearchParams,
} from './directoryQuery';
import './StudentsDirectory.css';

const SEARCH_DEBOUNCE_MS = 300;

// Activity filters in the order they are offered; 'online' needs exact presence (the API answers 400
// to it for a viewer who only gets days), so it is offered only when the response says so.
const ACTIVITY_VALUES = ['online', 'today', '7d', 'inactive3', 'inactive7', 'inactive14', 'inactive30', 'never'];

/**
 * The Directory tab of the Students hub: every student the signed-in staff member may open, across
 * all courses, paged and filtered on the server. The query is in the URL (same names and values as
 * the API's); the columns follow what the response grants (`meta`), so each role sees exactly the
 * fields the server sent and nothing is hidden or shown by role in this file.
 *
 * One toolbar row: search, course, status, activity and the at-risk toggle. The count, the sort and
 * the refresh sit in the header of the table card. On a phone the three dropdowns and the sort move
 * into one "Filters" bottom sheet.
 */
function StudentsDirectory() {
  const { t, lang } = useI18n();
  const { isAdminOrTeacher } = usePermissions();
  const phone = useIsPhone();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(() => parseDirectoryQuery(searchParams), [searchParams]);
  const queryRef = useRef(query);
  queryRef.current = query;
  const rootRef = useRef(null);
  const shownPage = useRef(query.page);

  const [view, setView] = useState({
    phase: 'loading', rows: [], meta: null, error: null,
  });
  const [reloadToken, setReloadToken] = useState(0);
  const [courseTitle, setCourseTitle] = useState({ id: '', title: '' });
  const [sheetOpen, setSheetOpen] = useState(false);

  const setQuery = useCallback((patch) => {
    setSearchParams(toSearchParams(patchDirectoryQuery(queryRef.current, patch)), { replace: true });
  }, [setSearchParams]);

  // ── Data ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setView((v) => ({
      ...v, phase: v.meta && v.phase !== 'error' ? 'refreshing' : 'loading', error: null,
    }));
    getStudents(query)
      .then((res) => {
        if (!cancelled) setView({ phase: 'ready', rows: res.data, meta: res.meta, error: null, forQuery: query });
      })
      .catch((error) => {
        if (!cancelled) setView((v) => ({ phase: 'error', rows: [], meta: v.meta, error }));
      });
    return () => { cancelled = true; };
  }, [query, reloadToken]);

  // A page past the end (rows were removed since the link was made) steps back to the last one.
  // Only an answer to the query now in the URL counts: right after the step back the old, empty
  // answer is still on screen until the next one lands.
  useEffect(() => {
    const {
      meta, rows, phase, forQuery,
    } = view;
    if (phase === 'ready' && forQuery === query && rows.length === 0 && meta?.total > 0 && query.page > 1) {
      setQuery({ page: Math.min(query.page - 1, Math.max(1, meta.totalPages || 1)) });
    }
  }, [view, query, setQuery]);

  // Turning a page brings the top of the list back into view (the pager is at the bottom).
  useEffect(() => {
    if (shownPage.current === query.page) return;
    shownPage.current = query.page;
    rootRef.current?.scrollIntoView?.({ block: 'start' });
  }, [query.page]);

  // The name of the course filter comes with the rows (each one carries its enrollment in that course).
  useEffect(() => {
    if (!query.courseId) {
      setCourseTitle((c) => (c.id ? { id: '', title: '' } : c));
      return;
    }
    const known = view.rows.find((row) => row.focus?.courseId === query.courseId)?.focus?.title;
    if (known) setCourseTitle({ id: query.courseId, title: known });
  }, [query.courseId, view.rows]);

  // ── Search box: typed text goes to the URL after a pause (or at once on Enter) ──
  const [searchText, setSearchText] = useState(query.search);
  const pushedSearch = useRef(query.search);

  useEffect(() => {
    // The URL changed under the box (Clear filters, a link): follow it.
    if (query.search !== pushedSearch.current) {
      pushedSearch.current = query.search;
      setSearchText(query.search);
    }
  }, [query.search]);

  const pushSearch = useCallback((text) => {
    const trimmed = text.trim();
    if (trimmed === pushedSearch.current) return;
    pushedSearch.current = trimmed;
    setQuery({ search: trimmed });
  }, [setQuery]);

  useEffect(() => {
    if (searchText.trim() === pushedSearch.current) return undefined;
    const timer = setTimeout(() => pushSearch(searchText), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText, pushSearch]);

  // ── What the response grants ───────────────────────────────────────────
  const { meta } = view;
  const columns = columnsFor(meta);
  const knownMeta = Boolean(meta);
  const showPresence = !knownMeta || columns.presence;
  const showLearning = !knownMeta || columns.learning;
  const exactPresence = meta?.presence === 'minute';
  const filters = activeFilterCount(query);
  // What the phone's "Filters" button holds: the course, the status and the activity (the sort is not a filter).
  const sheetFilters = (query.courseId ? 1 : 0) + (query.status ? 1 : 0) + (query.activity ? 1 : 0);

  const searchPlaceholder = !knownMeta
    ? t('student360.directory.searchPlaceholder')
    : columns.contact
      ? t('student360.directory.searchPlaceholderContact')
      : t('student360.directory.searchPlaceholderName');

  const statusOptions = [
    { value: '', label: t('student360.directory.status.any') },
    { value: 'active', label: t('student360.directory.status.active') },
    { value: 'completed', label: t('student360.directory.status.completed') },
    { value: 'inactive', label: t('student360.directory.status.inactive') },
    ...(meta?.leadPool && !query.courseId
      ? [{ value: 'none', label: t('student360.directory.status.none') }]
      : []),
  ];

  const activityOptions = [
    { value: '', label: t('student360.directory.activity.all') },
    ...ACTIVITY_VALUES
      .filter((value) => value !== 'online' || exactPresence)
      .map((value) => ({ value, label: t(`student360.directory.activity.${value}`) })),
  ];

  const sortOptions = [
    showPresence && { value: 'lastSeen', label: t('student360.directory.sort.lastSeen') },
    { value: 'name', label: t('student360.directory.sort.name') },
    { value: 'joined', label: t('student360.directory.sort.joined') },
    query.courseId && showLearning && { value: 'progress', label: t('student360.directory.sort.progress') },
    query.courseId && showLearning && { value: 'enrolled', label: t('student360.directory.sort.enrolled') },
  ].filter(Boolean);

  const clearFilters = () => {
    // The box goes too, including text typed but not yet sent to the URL.
    pushedSearch.current = '';
    setSearchText('');
    setSearchParams(toSearchParams(patchDirectoryQuery(query, {
      search: '', courseId: '', status: '', activity: '', atRisk: false,
    })), { replace: true });
  };

  // ── The controls (the toolbar on a laptop, the sheet on a phone) ───────
  const courseControl = query.courseId ? (
    <span className="ui-chip ui-chip--removable is-active sdir-course-chip">
      <span className="sdir-course-chip-label">
        {t('student360.directory.courseLabel')}
        {': '}
        <strong>
          {courseTitle.id === query.courseId && courseTitle.title
            ? courseTitle.title
            : t('student360.directory.selectedCourse')}
        </strong>
      </span>
      <button
        type="button"
        className="ui-chip__x"
        onClick={() => setQuery({ courseId: '' })}
        aria-label={t('student360.directory.clearCourse')}
      >
        <X aria-hidden="true" />
      </button>
    </span>
  ) : isAdminOrTeacher() ? (
    // The course list is an ADMIN, TEACHER and MENTOR route (GET /courses/admin); the other staff
    // accounts would only meet a 403 in the picker, so they filter by the other controls.
    <div className="sdir-course-picker">
      <SearchableCourseSelect
        value=""
        onChange={(courseId) => setQuery({ courseId })}
        placeholder={t('student360.directory.allCourses')}
        size={phone ? 'md' : 'sm'}
      />
    </div>
  ) : null;

  const statusControl = showLearning ? (
    <Dropdown
      value={query.status}
      onChange={(status) => setQuery({ status })}
      options={statusOptions}
      className="sdir-dd sdir-dd--status"
      ariaLabel={t('student360.directory.statusLabel')}
      size={phone ? 'md' : 'sm'}
    />
  ) : null;

  const activityControl = showPresence ? (
    <Dropdown
      value={query.activity}
      onChange={(activity) => setQuery({ activity })}
      options={activityOptions}
      className="sdir-dd sdir-dd--activity"
      ariaLabel={t('student360.directory.activityLabel')}
      size={phone ? 'md' : 'sm'}
    />
  ) : null;

  const sortControl = (
    <div className="sdir-sort">
      <span className="sdir-sort-label">{t('student360.directory.sortLabel')}</span>
      <Dropdown
        value={query.sort}
        onChange={(sort) => setQuery({ sort, order: defaultOrderFor(sort) })}
        options={sortOptions}
        className="sdir-dd sdir-dd--sort"
        ariaLabel={t('student360.directory.sortLabel')}
        size={phone ? 'md' : 'sm'}
      />
      <button
        type="button"
        className="ui-icon-btn sdir-order"
        onClick={() => setQuery({ order: query.order === 'asc' ? 'desc' : 'asc' })}
        aria-label={t('student360.directory.toggleOrder')}
        title={query.order === 'asc' ? t('student360.directory.orderAsc') : t('student360.directory.orderDesc')}
      >
        {query.order === 'asc' ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />}
      </button>
    </div>
  );

  const riskOn = query.atRisk;
  const riskChip = (!knownMeta || columns.learning) ? (
    <button
      type="button"
      className={`ui-chip sdir-chip-risk ${riskOn ? 'is-active' : ''}`}
      data-tone="danger"
      aria-pressed={riskOn}
      title={t('student360.atRiskHint')}
      onClick={() => setQuery({ atRisk: !riskOn })}
    >
      <TriangleAlert aria-hidden="true" />
      {t('student360.directory.atRiskFilter')}
    </button>
  ) : null;

  // ── Content ────────────────────────────────────────────────────────────
  const busy = view.phase === 'loading' || view.phase === 'refreshing';
  const forbidden = view.error?.code === 403;
  let content;
  if (view.phase === 'loading') {
    content = <DirectorySkeleton columns={columns} label={t('student360.directory.loading')} />;
  } else if (view.phase === 'error') {
    content = (
      <PageEmptyState
        variant={forbidden ? 'forbidden' : 'error'}
        title={forbidden ? t('student360.detail.noAccessTitle') : t('student360.errors.listTitle')}
        description={forbidden ? t('student360.errors.noAccessDesc') : t('student360.errors.desc')}
        actionLabel={forbidden ? undefined : t('student360.errors.retry')}
        onAction={forbidden ? undefined : () => setReloadToken((n) => n + 1)}
        secondaryLabel={filters > 0 ? t('student360.directory.clearFilters') : undefined}
        onSecondaryAction={filters > 0 ? clearFilters : undefined}
      />
    );
  } else if (view.rows.length === 0) {
    content = filters > 0 ? (
      <PageEmptyState
        variant="search"
        title={t('student360.directory.noMatchTitle')}
        description={t('student360.directory.noMatchDesc')}
        actionLabel={t('student360.directory.clearFilters')}
        onAction={clearFilters}
      />
    ) : (
      <PageEmptyState
        variant="students"
        title={t('student360.directory.emptyTitle')}
        description={t('student360.directory.emptyDesc')}
      />
    );
  } else {
    content = (
      <div
        className={`ui-table-card sdir-card ${view.phase === 'refreshing' ? 'is-refreshing' : ''}`}
        aria-busy={view.phase === 'refreshing'}
      >
        {view.phase === 'refreshing' && <span className="sdir-loadbar" aria-hidden="true" />}
        <div className="sdir-head">
          <div className="sdir-meta">
            <span className="sdir-count">{t('student360.directory.total', { count: meta?.total ?? view.rows.length })}</span>
            {meta?.generatedAt && (
              <span className="sdir-updated" title={t('student360.presence.tashkentTime')}>
                {t('student360.directory.updated', {
                  time: formatTime(meta.generatedAt, { lang, timeZone: STUDENT_TIME_ZONE }),
                })}
              </span>
            )}
            {filters > 0 && (
              <button type="button" className="sdir-clear" onClick={clearFilters}>
                {t('student360.directory.clearFilters')}
              </button>
            )}
          </div>
          <div className="sdir-head-tools">
            {!phone && sortControl}
            <button
              type="button"
              className="ui-icon-btn sdir-refresh"
              onClick={() => setReloadToken((n) => n + 1)}
              disabled={busy}
              aria-label={t('student360.directory.refresh')}
              title={t('student360.directory.refresh')}
            >
              <RotateCw className={busy ? 'is-spinning' : ''} aria-hidden="true" />
            </button>
          </div>
        </div>
        <TableScroll>
          <DirectoryTable
            rows={view.rows}
            meta={meta}
            now={nowFrom(meta)}
            courseId={query.courseId}
            onPickCourse={(courseId) => setQuery({ courseId })}
          />
        </TableScroll>
        {meta && meta.totalPages > 1 && (
          <div className="ui-table-footer">
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPrev={() => setQuery({ page: meta.page - 1 })}
              onNext={() => setQuery({ page: meta.page + 1 })}
              hideOnSinglePage
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="sdir" ref={rootRef}>
      <div className="sdir-toolbar">
        <label className="ds-search sdir-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') pushSearch(searchText); }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            maxLength={STUDENT_SEARCH_MAX_LENGTH}
            autoComplete="off"
            enterKeyHint="search"
          />
          {searchText && (
            <button
              type="button"
              className="ds-search-clear"
              onClick={() => { setSearchText(''); pushSearch(''); }}
              aria-label={t('common.clear')}
            >
              <X aria-hidden="true" />
            </button>
          )}
        </label>

        {phone ? (
          <button
            type="button"
            className="ds-btn ds-btn-secondary sdir-filter-toggle"
            aria-haspopup="dialog"
            onClick={() => setSheetOpen(true)}
          >
            <SlidersHorizontal aria-hidden="true" />
            {t('student360.filters.title')}
            {sheetFilters > 0 && <span className="ui-count ui-count--brand">{sheetFilters}</span>}
          </button>
        ) : (
          <>
            {courseControl}
            {statusControl}
            {activityControl}
          </>
        )}
        {riskChip}
      </div>

      {content}

      {phone && (
        <Modal
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={t('student360.filters.title')}
          size="sm"
          footer={(
            <>
              {filters > 0 && (
                <button type="button" className="ds-btn ds-btn-secondary" onClick={clearFilters}>
                  {t('student360.directory.clearFilters')}
                </button>
              )}
              <button type="button" className="ds-btn ds-btn-primary" onClick={() => setSheetOpen(false)}>
                {t('student360.filters.done')}
              </button>
            </>
          )}
        >
          <div className="sdir-sheet">
            {courseControl && (
              <div className="ui-field">
                <span className="ui-label">{t('student360.directory.courseLabel')}</span>
                {courseControl}
              </div>
            )}
            {statusControl && (
              <div className="ui-field">
                <span className="ui-label">{t('student360.directory.statusLabel')}</span>
                {statusControl}
              </div>
            )}
            {activityControl && (
              <div className="ui-field">
                <span className="ui-label">{t('student360.directory.activityLabel')}</span>
                {activityControl}
              </div>
            )}
            <div className="ui-field">
              <span className="ui-label">{t('student360.directory.sortLabel')}</span>
              {sortControl}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default StudentsDirectory;
